/// <reference lib="dom" />
export {}; // module scope
import { litresPerMFor, glycolPctFor } from "../shared/glycolData.js";

type Product = { variantId: number; sku: string; name: string; saleType: string | null; priceText: string; imageUrl: string | null; href: string };
type Data = { products: Product[]; lang: string; strings: Record<string, string> };

const dataEl = document.getElementById("glycol-data");
const results = document.getElementById("gl-results");
if (dataEl?.textContent && results) {
  try {
    boot(JSON.parse(dataEl.textContent) as Data, results);
  } catch {
    /* malformed */
  }
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function bumpCount(n: number) {
  document.querySelectorAll("[data-cart-count]").forEach((el) => {
    const c = parseInt(el.textContent || "0", 10) || 0;
    el.textContent = String(c + n);
    (el as HTMLElement).classList.remove("hidden");
  });
}

function boot(data: Data, results: HTMLElement) {
  const s = data.strings;
  const t = (k: string) => s[k] ?? k;
  const locale = data.lang === "de" ? "de-DE" : "en-US";
  const nf1 = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const ids = ["gl-dn", "gl-length", "gl-extra", "gl-frost"];
  const els = Object.fromEntries(ids.map((id) => [id, document.getElementById(id) as HTMLInputElement])) as Record<string, HTMLInputElement>;
  for (const id of ids) {
    els[id]?.addEventListener("input", render);
    els[id]?.addEventListener("change", render);
  }
  results.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-add-cart]") as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    void addToCart(Number(btn.dataset.vid), btn.dataset.unit ? Number(btn.dataset.unit) : undefined, btn);
  });
  render();

  function render() {
    const dn = Number(els["gl-dn"]?.value);
    const length = Number(els["gl-length"]?.value) || 0;
    const extra = Number(els["gl-extra"]?.value) || 0;
    const frost = Number(els["gl-frost"]?.value);
    const volume = length * litresPerMFor(dn) + extra;
    const pct = glycolPctFor(frost);
    const glycol = volume * pct;
    const water = Math.max(0, volume - glycol);

    const litres = Math.max(1, Math.ceil(glycol));
    const cards = data.products
      .map((p) => {
        const isVolume = p.saleType === "volume" || p.saleType === "length";
        const unitAttr = isVolume ? ` data-unit="${litres}"` : "";
        const label = isVolume ? `${esc(t("prod_add"))} ${litres} L` : esc(t("prod_add"));
        return `<div class="card flex gap-3 p-3">
          ${p.imageUrl ? `<img src="${esc(p.imageUrl)}" alt="${esc(p.sku)}" class="h-16 w-16 rounded object-contain"/>` : ""}
          <div class="min-w-0 flex-1">
            <a href="${esc(p.href)}" class="block font-medium text-slate-900">${esc(p.name)}</a>
            <div class="text-xs text-slate-400">${esc(p.sku)} · ${esc(p.priceText)}</div>
            <button type="button" data-add-cart data-vid="${p.variantId}"${unitAttr} class="btn-primary mt-2 inline-flex items-center gap-1 text-xs"><i class="fa-solid fa-cart-plus"></i> ${label}</button>
          </div>
        </div>`;
      })
      .join("");

    results.innerHTML = `
      <div class="card p-4">
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4 text-center">
          <div><div class="text-2xl font-semibold text-slate-900">${nf1.format(volume)}</div><div class="text-xs text-slate-500">${esc(t("volume"))}</div></div>
          <div><div class="text-2xl font-semibold text-brand-700">${nf1.format(glycol)}</div><div class="text-xs text-slate-500">${esc(t("glycol_l"))}</div></div>
          <div><div class="text-2xl font-semibold text-slate-900">${nf1.format(water)}</div><div class="text-xs text-slate-500">${esc(t("water_l"))}</div></div>
          <div><div class="text-2xl font-semibold text-slate-900">${Math.round(pct * 100)}%</div><div class="text-xs text-slate-500">${esc(t("mix"))}</div></div>
        </div>
        <p class="mt-2 text-[11px] text-slate-400">${esc(t("estimate_note"))}</p>
      </div>
      ${data.products.length ? `<h2 class="mt-4 text-base font-semibold text-slate-700">${esc(t("matched"))}</h2><div class="mt-2 grid gap-3 sm:grid-cols-2">${cards}</div>` : `<p class="mt-3 text-sm text-slate-500">${esc(t("no_products"))}</p>`}`;
  }

  async function addToCart(variantId: number, unitAmount: number | undefined, btn: HTMLButtonElement) {
    if (!variantId) return;
    btn.disabled = true;
    const body: Record<string, string> = { variantId: String(variantId), qty: "1" };
    if (unitAmount && unitAmount > 0) body.unitAmount = String(unitAmount);
    try {
      const r = await fetch("/cart/add", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
        body: new URLSearchParams(body),
      });
      if (r.ok) {
        bumpCount(1);
        btn.innerHTML = `<i class="fa-solid fa-check"></i> ${esc(t("prod_added"))}`;
      }
    } catch {
      /* ignore */
    } finally {
      setTimeout(() => { btn.disabled = false; }, 1500);
    }
  }
}
